import { ExtendedObject } from '../hooks/objectHooks';

/**
 * Extracts the base name and page number from a document name.
 * @param name The document name to process.
 * @returns An object with baseName and pageNum properties.
 */
const extractBaseNameAndPageNum = (name: string): { baseName: string; pageNum: number } => {
  const nameLower = name.toLowerCase();
  // Find last occurrence of "page_" pattern
  const pageIndex = nameLower.lastIndexOf('page_');

  if (pageIndex !== -1) {
    // Extract everything after "page_"
    const suffix = nameLower.substring(pageIndex + 5);
    // Try to convert to number
    const pageNum = parseInt(suffix, 10);

    if (!isNaN(pageNum)) {
      // The base name is everything before "page_"
      return {
        baseName: nameLower.substring(0, pageIndex),
        pageNum,
      };
    }
  }

  // If no page pattern found, check for last underscore
  const lastUnderscoreIndex = nameLower.lastIndexOf('_');
  if (lastUnderscoreIndex !== -1) {
    const suffix = nameLower.substring(lastUnderscoreIndex + 1);
    const pageNum = parseInt(suffix, 10);

    if (!isNaN(pageNum)) {
      return {
        baseName: nameLower.substring(0, lastUnderscoreIndex),
        pageNum,
      };
    }
  }

  // If no page number found, use the full name and a high number
  // so it appears after numbered items
  return {
    baseName: nameLower,
    pageNum: 10000, // A high number to sort non-page items last
  };
};

/**
 * Sorts an array of objects by grouping related documents first, then sorting by page number.
 * Documents are grouped by their base name (excluding the page number part).
 * @param array The array to sort.
 * @returns A new array sorted by document group and page number.
 */
export const sortByPageNum = <T extends ExtendedObject>(array: T[]): T[] => {
  // Create a copy of the array to avoid mutating the original
  return [...array].sort((a, b) => {
    // Extract base names and page numbers
    const { baseName: baseNameA, pageNum: pageNumA } = extractBaseNameAndPageNum(a.objectname);
    const { baseName: baseNameB, pageNum: pageNumB } = extractBaseNameAndPageNum(b.objectname);

    // First compare base names (alphabetically)
    const baseNameComparison = baseNameA.localeCompare(baseNameB);

    // If base names are different, group by base name
    if (baseNameComparison !== 0) {
      return baseNameComparison;
    }

    // If base names are the same, sort by page number
    return pageNumA - pageNumB;
  });
};
